// Date-range helpers for the date filters. Ranges are computed in the events'
// timezone (Europe/Paris) so "today"/"this weekend" line up with when events
// actually happen, regardless of the viewer's device timezone.

import { SITE } from '@site';

import { formatDayMonth } from './i18n/format';
import type { Locale } from './i18n/locales';

export const APP_TZ = SITE.timeZone;

type Ymd = { year: number; month: number; day: number };

function ymdInTz(date: Date, timeZone: string): Ymd & { hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const m: Record<string, number> = {};
  for (const p of parts) if (p.type !== 'literal') m[p.type] = Number(p.value);
  return {
    year: m.year,
    month: m.month,
    day: m.day,
    hour: m.hour,
    minute: m.minute,
    second: m.second,
  };
}

// Convert a wall-clock time in `timeZone` to the corresponding UTC instant.
function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  timeZone: string,
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, 0, 0);
  const back = ymdInTz(new Date(utcGuess), timeZone);
  const tzAsUtc = Date.UTC(back.year, back.month - 1, back.day, back.hour, back.minute, back.second);
  const offset = tzAsUtc - utcGuess;
  return new Date(utcGuess - offset);
}

// Midnight (start of day) in `timeZone` for the calendar day containing `date`.
function startOfDayTz(date: Date, timeZone: string): Date {
  const p = ymdInTz(date, timeZone);
  return zonedTimeToUtc(p.year, p.month, p.day, 0, timeZone);
}

// Add `n` whole days to a zoned day-start, staying on the correct calendar day
// across DST changes (a ~3h cushion keeps us clear of the ±1h shift).
function addDaysTz(dayStart: Date, n: number, timeZone: string): Date {
  const anchor = new Date(dayStart.getTime() + n * 86400000 + 3 * 3600 * 1000);
  return startOfDayTz(anchor, timeZone);
}

export type DateRange = { from: Date; until: Date };

// "All time" spans every upcoming event. The lower bound is the start of today
// (below), NOT the epoch: past events aren't reliably gone from the DB — the
// archive job now keeps ended events for a 30-day retention window (for their
// SEO value — see isEventPast below) before pruning them, so past-dated rows
// routinely linger, and a 1970 lower bound would surface them.
// Using todayStart keeps the react-query key stable within a day (a raw
// `new Date()` would change it every render) while excluding anything before
// today. The upper bound stays far in the future to mean "everything ahead".
const ALL_TIME_UNTIL = new Date('2100-01-01T00:00:00.000Z');

export function getDateRange(dateId: string, customDate?: Date | null): DateRange {
  const todayStart = startOfDayTz(new Date(), APP_TZ);

  switch (dateId) {
    case 'alltime':
      return { from: todayStart, until: ALL_TIME_UNTIL };

    case 'today':
      return { from: todayStart, until: addDaysTz(todayStart, 1, APP_TZ) };

    case 'tomorrow': {
      const from = addDaysTz(todayStart, 1, APP_TZ);
      return { from, until: addDaysTz(from, 1, APP_TZ) };
    }

    case 'weekend': {
      const p = ymdInTz(todayStart, APP_TZ);
      const weekday = new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay(); // 0 Sun .. 6 Sat
      let fromOffset: number;
      let untilOffset: number;
      if (weekday === 6) {
        fromOffset = 0; // Saturday → Sat + Sun
        untilOffset = 2;
      } else if (weekday === 0) {
        fromOffset = 0; // Sunday → just Sunday
        untilOffset = 1;
      } else {
        fromOffset = 6 - weekday; // upcoming Saturday
        untilOffset = fromOffset + 2;
      }
      return {
        from: addDaysTz(todayStart, fromOffset, APP_TZ),
        until: addDaysTz(todayStart, untilOffset, APP_TZ),
      };
    }

    // The /events filters' rolling windows (hub-filters.ts), from today on.
    case 'week':
      return { from: todayStart, until: addDaysTz(todayStart, 7, APP_TZ) };

    case 'month':
      return { from: todayStart, until: addDaysTz(todayStart, 30, APP_TZ) };

    case 'custom': {
      const base = customDate ?? new Date();
      const from = startOfDayTz(base, APP_TZ);
      return { from, until: addDaysTz(from, 1, APP_TZ) };
    }

    default:
      return { from: todayStart, until: addDaysTz(todayStart, 1, APP_TZ) };
  }
}

// Minimal shape of react-i18next's `t`, to keep this module free of i18n deps.
type Translate = (key: string, options?: Record<string, unknown>) => string;

// Localized lowercase phrase used inside headings/stats, e.g. "today",
// "this weekend", "on 4 Aug". Empty for "all time". Pass the `t` from
// useTranslation (for re-render on language change) and the active locale
// (for the Intl-formatted "on 4 Aug" case).
export function dateHeadingPhrase(
  dateId: string,
  customDate: Date | null | undefined,
  t: Translate,
  locale: Locale,
): string {
  switch (dateId) {
    case 'alltime':
      return '';
    case 'tomorrow':
      return t('dates.tomorrow');
    case 'weekend':
      return t('dates.thisWeekend');
    case 'custom':
      return t('dates.on', { date: formatDayMonth(customDate ?? new Date(), APP_TZ, locale) });
    case 'today':
    default:
      return t('dates.today');
  }
}

// Upcoming days (starting today) for the "Pick a date" chooser.
export function upcomingDays(count: number): Date[] {
  const start = startOfDayTz(new Date(), APP_TZ);
  return Array.from({ length: count }, (_, i) => addDaysTz(start, i, APP_TZ));
}

// True when both instants fall on the same Europe/Paris calendar day.
export function isSameDay(a: Date, b: Date): boolean {
  const pa = ymdInTz(a, APP_TZ);
  const pb = ymdInTz(b, APP_TZ);
  return pa.year === pb.year && pa.month === pb.month && pa.day === pb.day;
}

// True once an event's end (or start, if it has no end) falls before today —
// mirrors archive_past_events()'s cutoff exactly (20260916120000_archive_events_30day_window.sql),
// so this flips at the same moment the row starts its 30-day retention window
// on its way to being pruned. Drives the "this event has ended" UI state and
// the noindex,follow robots meta in EventHead.
export function isEventPast(event: { start_ts: string; end_ts: string | null }): boolean {
  const end = new Date(event.end_ts ?? event.start_ts);
  return end.getTime() < startOfDayTz(new Date(), APP_TZ).getTime();
}
